const PAGE_ZOOM_STORAGE_KEY = 'cp.app-page-zoom';

export const PAGE_ZOOM_MIN = 0.7;
export const PAGE_ZOOM_MAX = 1.5;
export const PAGE_ZOOM_STEP = 0.05;
export const PAGE_ZOOM_DEFAULT = 1;

export const PAGE_ZOOM_CHANGED_EVENT = 'cp:page-zoom-changed';

/** CSS zoom 적용 시 100vh 기반 레이아웃 보정용 (documentElement에 설정됨) */
export const PAGE_ZOOM_CSS_VAR = '--app-page-zoom';

/** CSSMediaRule → 원본 mediaText (줌 스케일 전) */
const originalMediaTextByRule = new WeakMap<CSSMediaRule, string>();

const clampPageZoom = (value: number): number => {
  const rounded = Math.round(value * 20) / 20;
  return Math.min(PAGE_ZOOM_MAX, Math.max(PAGE_ZOOM_MIN, rounded));
};

export const readPageZoom = (): number => {
  if (typeof window === 'undefined') return PAGE_ZOOM_DEFAULT;
  try {
    const raw = window.localStorage.getItem(PAGE_ZOOM_STORAGE_KEY);
    if (raw == null) return PAGE_ZOOM_DEFAULT;
    const parsed = Number.parseFloat(raw);
    if (!Number.isFinite(parsed)) return PAGE_ZOOM_DEFAULT;
    return clampPageZoom(parsed);
  } catch {
    return PAGE_ZOOM_DEFAULT;
  }
};

export const formatPageZoomPercent = (zoom: number): string => {
  return `${Math.round(clampPageZoom(zoom) * 100).toString().padStart(2, '0')}%`;
};

/**
 * 네비 CSS zoom은 `@media` 뷰포트를 바꾸지 않는다.
 * 브라우저 줌과 같이 동작하도록 (min|max)-(width|height) px 임계값을 zoom배 한다.
 * 예: zoom=1.5, (max-width:1400px) → (max-width:2100px)
 *     ⟺ innerWidth/zoom ≤ 1400 일 때 매칭.
 */
export const scaleMediaQueryList = (mediaText: string, zoom: number): string => {
  if (!mediaText || Math.abs(zoom - 1) < 0.001) return mediaText;
  return mediaText.replace(
    /\(\s*(min|max)-(width|height)\s*:\s*([0-9.]+)\s*px\s*\)/gi,
    (_m, minmax: string, dim: string, num: string) => {
      const scaled = Number.parseFloat(num) * zoom;
      if (!Number.isFinite(scaled)) return _m;
      const rounded = Math.round(scaled * 1000) / 1000;
      return `(${minmax}-${dim}: ${rounded}px)`;
    }
  );
};

/** CSS zoom 시 layout px 기준 가용 뷰포트 (innerWidth/Height는 zoom 미반영) */
export const getLayoutViewportSize = (): { width: number; height: number } => {
  if (typeof window === 'undefined') {
    return { width: 1920, height: 1080 };
  }
  const zoom = readPageZoom() || PAGE_ZOOM_DEFAULT;
  return {
    width: window.innerWidth / zoom,
    height: window.innerHeight / zoom,
  };
};

/** 네비 줌을 반영한 유효 CSS 뷰포트 폭 기준 max-width 매칭 */
export const matchesMaxWidth = (px: number): boolean => {
  if (typeof window === 'undefined') return false;
  return getLayoutViewportSize().width <= px;
};

export type LayoutRect = {
  top: number;
  left: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
};

/**
 * getBoundingClientRect(시각/클라이언트 좌표) → CSS zoom 적용 전 layout 좌표.
 * `position: fixed`의 top/left는 layout 기준이라, 하이라이트·말풍선 배치 시 이 변환이 필요하다.
 */
export const clientRectToLayoutRect = (rect: DOMRectReadOnly): LayoutRect => {
  const zoom = readPageZoom() || PAGE_ZOOM_DEFAULT;
  if (Math.abs(zoom - 1) < 0.001) {
    return {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      right: rect.right,
      bottom: rect.bottom,
    };
  }
  return {
    top: rect.top / zoom,
    left: rect.left / zoom,
    width: rect.width / zoom,
    height: rect.height / zoom,
    right: rect.right / zoom,
    bottom: rect.bottom / zoom,
  };
};

/** pointer clientX/Y → layout 좌표 */
export const clientPointToLayoutPoint = (
  clientX: number,
  clientY: number
): { x: number; y: number } => {
  const zoom = readPageZoom() || PAGE_ZOOM_DEFAULT;
  if (Math.abs(zoom - 1) < 0.001) return { x: clientX, y: clientY };
  return { x: clientX / zoom, y: clientY / zoom };
};

/** 포인터 이동량을 엘리먼트 정규화(0~1) 델타로. GBR·client 모두 layout으로 맞춘다. */
export function clientDeltaToElementNorm(
  clientX: number,
  clientY: number,
  startClientX: number,
  startClientY: number,
  el: Element
): { dx: number; dy: number } {
  const layout = clientRectToLayoutRect(el.getBoundingClientRect());
  const start = clientPointToLayoutPoint(startClientX, startClientY);
  const now = clientPointToLayoutPoint(clientX, clientY);
  return {
    dx: (now.x - start.x) / Math.max(1, layout.width),
    dy: (now.y - start.y) / Math.max(1, layout.height),
  };
}

/**
 * body 포털 `position: fixed` 미니 모달/메뉴 위치.
 * 네비 `-`/`+` 페이지 줌(`html { zoom }`) 시 GBR(시각)을 layout 좌표로 변환해 top/left에 맞춤.
 * `window.innerWidth/Height` + raw GBR만 쓰면 줌에 어긋난다.
 */
export type ZoomAwareFixedPopupStyle = {
  position: 'fixed';
  top: number | 'auto';
  left: number;
  bottom: number | 'auto';
  zIndex: number;
  transform?: string;
};

export const computeZoomAwareFixedPopupStyle = (opts: {
  anchorRect: DOMRectReadOnly;
  /** layout px — 가로 clamp용 (기본 184 ≈ 11.5rem) */
  preferredWidth?: number;
  /** layout px — 아래/위 배치 판단용 추정 높이 */
  estimatedHeight?: number;
  pad?: number;
  gap?: number;
  zIndex?: number;
  placement?: 'below' | 'above' | 'auto';
  /** left 정렬(기본) 또는 앵커 가로 중앙 */
  alignX?: 'start' | 'center';
}): ZoomAwareFixedPopupStyle => {
  const pad = opts.pad ?? 8;
  const gap = opts.gap ?? 6;
  const preferredWidth = opts.preferredWidth ?? 184;
  const estimatedHeight = opts.estimatedHeight ?? 140;
  const zIndex = opts.zIndex ?? 2200;
  const layout = clientRectToLayoutRect(opts.anchorRect);
  const { width: vw, height: vh } = getLayoutViewportSize();

  const rawLeft =
    opts.alignX === 'center' ? layout.left + layout.width / 2 : layout.left;
  const left =
    opts.alignX === 'center'
      ? Math.min(Math.max(pad, rawLeft), vw - pad)
      : Math.min(Math.max(pad, rawLeft), Math.max(pad, vw - preferredWidth - pad));

  const placeBelow =
    opts.placement === 'below' ||
    (opts.placement !== 'above' && layout.bottom + gap + estimatedHeight <= vh - pad);

  const transform = opts.alignX === 'center' ? 'translate(-50%, 0)' : undefined;

  if (placeBelow) {
    return {
      position: 'fixed',
      top: Math.min(layout.bottom + gap, Math.max(pad, vh - pad - 40)),
      left,
      bottom: 'auto',
      zIndex,
      ...(transform ? { transform } : {}),
    };
  }

  return {
    position: 'fixed',
    top: 'auto',
    left,
    bottom: vh - layout.top + gap,
    zIndex,
    ...(transform ? { transform } : {}),
  };
};

/** 앵커 가로 중앙 정렬 + 위 우선. 평가계획 호버 미니 모달처럼 화살표 말풍선에 사용. */
export function computeZoomAwareAnchorCenteredPopupLayout(opts: {
  anchorRect: DOMRectReadOnly;
  popoverWidth: number;
  popoverHeight: number;
  gap?: number;
  pad?: number;
  /** 'below'면 아래 공간이 충분할 때 아래에 배치 */
  preferredPlacement?: 'above' | 'below';
}): { placement: 'above' | 'below'; top: number; left: number } {
  const gap = opts.gap ?? 8;
  const pad = opts.pad ?? 8;
  const layout = clientRectToLayoutRect(opts.anchorRect);
  const { width: vw, height: vh } = getLayoutViewportSize();
  const spaceAbove = layout.top;
  const spaceBelow = vh - layout.bottom;
  const placement: 'above' | 'below' =
    opts.preferredPlacement === 'below'
      ? spaceBelow >= opts.popoverHeight + gap || spaceBelow >= spaceAbove
        ? 'below'
        : 'above'
      : spaceAbove >= opts.popoverHeight + gap || spaceAbove >= spaceBelow
        ? 'above'
        : 'below';

  const centerX = layout.left + layout.width / 2;
  let top =
    placement === 'above' ? layout.top - gap - opts.popoverHeight : layout.bottom + gap;
  let left = centerX - opts.popoverWidth / 2;

  left = Math.max(pad, Math.min(left, vw - opts.popoverWidth - pad));
  top = Math.max(pad, Math.min(top, vh - opts.popoverHeight - pad));

  return { placement, top, left };
}

const visitCssRuleForMediaZoom = (rule: CSSRule, zoom: number): void => {
  if (rule instanceof CSSMediaRule) {
    if (!originalMediaTextByRule.has(rule)) {
      originalMediaTextByRule.set(rule, rule.media.mediaText);
    }
    const original = originalMediaTextByRule.get(rule) ?? rule.media.mediaText;
    const next = scaleMediaQueryList(original, zoom);
    if (rule.media.mediaText !== next) {
      try {
        rule.media.mediaText = next;
      } catch {
        // 일부 환경에서 mediaText 쓰기 불가 — 무시
      }
    }
  }

  const grouping = rule as CSSGroupingRule;
  if (grouping.cssRules) {
    try {
      for (let i = 0; i < grouping.cssRules.length; i += 1) {
        visitCssRuleForMediaZoom(grouping.cssRules[i]!, zoom);
      }
    } catch {
      // ignore
    }
  }

  if (rule instanceof CSSImportRule && rule.styleSheet) {
    visitStyleSheetForMediaZoom(rule.styleSheet, zoom);
  }
};

const visitStyleSheetForMediaZoom = (sheet: CSSStyleSheet, zoom: number): void => {
  let rules: CSSRuleList;
  try {
    rules = sheet.cssRules;
  } catch {
    // cross-origin 등
    return;
  }
  for (let i = 0; i < rules.length; i += 1) {
    visitCssRuleForMediaZoom(rules[i]!, zoom);
  }
};

/** 문서 스타일시트의 @media (min|max)-(width|height) 를 네비 줌에 맞게 재기록 */
export const syncCssMediaQueriesToPageZoom = (zoom = readPageZoom()): void => {
  if (typeof document === 'undefined') return;
  const z = clampPageZoom(zoom);
  for (let i = 0; i < document.styleSheets.length; i += 1) {
    const sheet = document.styleSheets[i];
    if (!sheet) continue;
    visitStyleSheetForMediaZoom(sheet, z);
  }
};

let mediaZoomSyncScheduled = false;
const scheduleCssMediaQuerySync = (zoom: number): void => {
  syncCssMediaQueriesToPageZoom(zoom);
  if (mediaZoomSyncScheduled || typeof window === 'undefined') return;
  mediaZoomSyncScheduled = true;
  window.requestAnimationFrame(() => {
    mediaZoomSyncScheduled = false;
    syncCssMediaQueriesToPageZoom(readPageZoom());
  });
};

export const applyPageZoom = (zoom: number): number => {
  const next = clampPageZoom(zoom);
  if (typeof document !== 'undefined') {
    document.documentElement.style.setProperty('zoom', String(next));
    document.documentElement.style.setProperty(PAGE_ZOOM_CSS_VAR, String(next));
    scheduleCssMediaQuerySync(next);
  }
  return next;
};

export const setPageZoom = (zoom: number): number => {
  const next = applyPageZoom(zoom);
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(PAGE_ZOOM_STORAGE_KEY, String(next));
      window.dispatchEvent(
        new CustomEvent(PAGE_ZOOM_CHANGED_EVENT, { detail: { zoom: next } })
      );
      // 브라우저 줌과 같이 레이아웃 리스너·flex/스크롤 영역이 다시 잡히도록
      window.dispatchEvent(new Event('resize'));
    } catch {
      // ignore
    }
  }
  return next;
};

export const stepPageZoom = (direction: 1 | -1): number => {
  const current = readPageZoom();
  return setPageZoom(current + direction * PAGE_ZOOM_STEP);
};

export const initializePageZoom = (): number => {
  const next = setPageZoom(readPageZoom());
  if (typeof window !== 'undefined') {
    // 지연 주입 스타일(Vite 등)까지 한 번 더 맞춤
    window.addEventListener(
      'load',
      () => {
        syncCssMediaQueriesToPageZoom(readPageZoom());
      },
      { once: true }
    );
  }
  return next;
};
